/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { children, Component, JSXElement, Show, For } from 'solid-js';
export interface PanelLayoutProps {
  className?: string;
  headerClassName?: string;
  panelTitle?: string;
  customTitle?: JSXElement;
  titleDataCy?: string;
  titleSectionRight?: JSXElement;
  leftActions?: JSXElement | JSXElement[];
  rightActions?: JSXElement | JSXElement[];
  content?: JSXElement;
  subHeaderContent?: JSXElement;
  contentRef?(element: HTMLDivElement): void;
}

const PanelLayout: Component<PanelLayoutProps> = (props) => {
  // `props.content` is a lazy getter that re-creates the JSX on every access;
  // resolve it once so testing it in `Show` doesn't mount a second, orphaned
  // copy of the content tree.
  const resolvedContent = children(() => props.content);
  const getRootClass = () =>
    ['absolute inset-0 flex flex-col h-full bg-base-page', props.className]
      .filter(Boolean)
      .join(' ');

  const getHeaderSectionClass = () => {
    const base =
      'px-1 flex flex-nowrap shrink-0 items-center border-b border-gray-2';
    return !props.subHeaderContent
      ? [base, 'py-3'].join(' ')
      : [base, 'py-3 !pb-0'].join(' ');
  };

  const getLeftActions = () => {
    const actions = props.leftActions;
    if (!actions) return [] as JSXElement[];
    return Array.isArray(actions) ? actions : [actions];
  };

  const getRightActions = () => {
    const actions = props.rightActions;
    if (!actions) return [] as JSXElement[];
    return Array.isArray(actions) ? actions : [actions];
  };

  return (
    <div class={getRootClass()} data-testid="panel-layout">
      <div class={props.headerClassName ?? getHeaderSectionClass()}>
        <For each={getLeftActions()}>{(node) => <>{node}</>}</For>
        <div class="flex flex-col grow min-w-0">
          <Show
            when={props.customTitle}
            fallback={
              <span
                class="inline-block leading-tight font-semibold truncate"
                // eslint-disable-next-line @typescript-eslint/naming-convention -- SolidJS CSSProperties requires kebab-case
                style={{ 'font-size': '1.375rem' }}
                data-cy={props.titleDataCy}
                data-testid={props.titleDataCy}
              >
                {props.panelTitle}
              </span>
            }
          >
            {props.customTitle}
          </Show>
          <Show when={props.subHeaderContent}>{props.subHeaderContent}</Show>
          {props.titleSectionRight}
        </div>
        <For each={getRightActions()}>{(node) => <>{node}</>}</For>
      </div>
      <Show when={resolvedContent()} fallback={<slot />}>
        <div
          ref={(element) => {
            props.contentRef?.(element);
          }}
          class="grow min-h-0 overflow-y-auto overflow-x-hidden"
        >
          {resolvedContent()}
        </div>
      </Show>
    </div>
  );
};

export default PanelLayout;
